import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import "../styles/dashboard.css";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";
const AUTH_NAME_STORAGE_KEY = "authName";
const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }
  if (cleaned === "TANNERYADMIN") {
    return "TANNERY_ADMIN";
  }

  return cleaned;
};

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const getApiMessage = async (response, fallbackMessage) => {
  const responseText = await response.text();
  if (!responseText) {
    return fallbackMessage;
  }

  try {
    const payload = JSON.parse(responseText);
    return payload?.message || fallbackMessage;
  } catch (parseError) {
    return responseText;
  }
};

const getRoleLabel = (role) => {
  if (role === "SUPER_ADMIN") {
    return "Super Admin";
  }
  if (role === "TANNERY_ADMIN") {
    return "Tannery Admin";
  }
  if (role === "DATA_ENTRY" || role === "DATAENTRY") {
    return "Data Entry";
  }
  return "User";
};

const getDisplayName = () => {
  const storedName =
    localStorage.getItem(AUTH_NAME_STORAGE_KEY) ||
    localStorage.getItem("username") ||
    localStorage.getItem("userName") ||
    localStorage.getItem("fullName");

  if (storedName && storedName.trim()) {
    return storedName.trim();
  }

  return "Ahmed Malik";
};

const getInitials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "AM";

function DashboardPage() {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const userRole = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY) || "");
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const isTanneryAdmin = userRole === "TANNERY_ADMIN";
  const isDataEntry = userRole === "DATA_ENTRY" || userRole === "DATAENTRY";
  const isExecutive = userRole === "EXECUTIVE";
  const displayName = getDisplayName();
  const profileInitials = getInitials(displayName);
  const roleLabel = getRoleLabel(userRole);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileNavOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleLogout = async () => {
    const baseApiUrl = getBaseApiUrl();
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);

    try {
      setIsLoggingOut(true);
      let logoutMessage = "Logged out successfully.";

      if (token) {
        const response = await fetch(`${baseApiUrl}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        logoutMessage = await getApiMessage(response, logoutMessage);

        if (!response.ok) {
          throw new Error(logoutMessage || "Unable to logout right now.");
        }
      }

      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(AUTH_ROLE_STORAGE_KEY);
      localStorage.removeItem(AUTH_NAME_STORAGE_KEY);
      navigate("/", {
        replace: true,
        state: { logoutMessage },
      });
    } catch (error) {
      console.error("Logout request failed:", error);
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(AUTH_ROLE_STORAGE_KEY);
      localStorage.removeItem(AUTH_NAME_STORAGE_KEY);
      navigate("/", {
        replace: true,
        state: {
          logoutMessage: error.message || "Logged out locally.",
        },
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <main className="dashboard-page">
      <div className="dashboard-layout">
        <aside className="dashboard-sidebar">
          <div className="sidebar-brand-row">
            <div className="sidebar-brand">
              <p className="sidebar-title">AlKhidmat</p>
              <p className="sidebar-subtitle">TANNERY SYSTEM</p>
            </div>
            <button
              type="button"
              className="sidebar-menu-toggle"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileNavOpen}
              aria-controls="dashboard-nav-menu"
              onClick={() => setIsMobileNavOpen((prev) => !prev)}
            >
              <span className="sidebar-menu-toggle-line"></span>
              <span className="sidebar-menu-toggle-line"></span>
              <span className="sidebar-menu-toggle-line"></span>
            </button>
          </div>

          <nav
            id="dashboard-nav-menu"
            className={`sidebar-nav ${isMobileNavOpen ? "sidebar-nav-open" : ""}`}
            aria-label="Dashboard navigation"
          >
            <NavLink
              to="/dashboard"
              end
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
              }
              onClick={() => setIsMobileNavOpen(false)}
            >
              Dashboard
            </NavLink>
            {isSuperAdmin ? (
              <NavLink
                to="/dashboard/create-tannery"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                }
                onClick={() => setIsMobileNavOpen(false)}
              >
                Tannery
              </NavLink>
            ) : null}
            {isSuperAdmin ? (
              <NavLink
                to="/dashboard/super-admin-users"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                }
                onClick={() => setIsMobileNavOpen(false)}
              >
                Users
              </NavLink>
            ) : null}
            {isSuperAdmin ? (
              <NavLink
                to="/dashboard/super-admin-tokens"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                }
                onClick={() => setIsMobileNavOpen(false)}
              >
                All tokens
              </NavLink>
            ) : null}
            {isTanneryAdmin ? (
              <NavLink
                to="/dashboard/create-users"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                }
                onClick={() => setIsMobileNavOpen(false)}
              >
                Users
              </NavLink>
            ) : null}
            {isDataEntry || isTanneryAdmin ? (
              <NavLink
                to="/dashboard/tokens"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                }
                onClick={() => setIsMobileNavOpen(false)}
              >
                Tokens
              </NavLink>
            ) : null}
            {isSuperAdmin || isTanneryAdmin ? (
              <NavLink
                to="/dashboard/logs"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                }
                onClick={() => setIsMobileNavOpen(false)}
              >
                Logs
              </NavLink>
            ) : null}
            {isSuperAdmin || isTanneryAdmin || isDataEntry || isExecutive ? (
              <NavLink
                to="/dashboard/score"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                }
                onClick={() => setIsMobileNavOpen(false)}
              >
                Score
              </NavLink>
            ) : null}
            {isSuperAdmin ? (
              <NavLink
                to="/dashboard/zila-management"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                }
                onClick={() => setIsMobileNavOpen(false)}
              >
                Zila Management
              </NavLink>
            ) : null}
            <div className="profile-menu-wrapper navbar-profile-menu" ref={profileMenuRef}>
              <button
                type="button"
                className="profile-menu-trigger"
                aria-haspopup="menu"
                aria-expanded={isProfileMenuOpen}
                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
              >
                <span className="dashboard-avatar-pill">{profileInitials}</span>
                <span className="navbar-profile-text">
                  <span className="navbar-profile-name">{displayName}</span>
                  <span className="navbar-profile-role">{roleLabel}</span>
                </span>
              </button>
              {isProfileMenuOpen ? (
                <div className="profile-menu-dropdown" role="menu" aria-label="Profile menu">
                  {isSuperAdmin || isTanneryAdmin ? (
                    <button
                      type="button"
                      className="profile-menu-item"
                      role="menuitem"
                      onClick={() => {
                        navigate("/dashboard/settings");
                        setIsProfileMenuOpen(false);
                      }}
                    >
                      Settings
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="profile-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      handleLogout();
                    }}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? "Logging out..." : "Logout"}
                  </button>
                </div>
              ) : null}
            </div>
          </nav>
        </aside>

        <div className="dashboard-main">
          <Outlet />
        </div>
      </div>
    </main>
  );
}

export default DashboardPage;
